/**
 * Standalone outbound-labels enums drained out of assorted `redo/model`
 * files (util, auto-merge, surcharge, learned-preset, fulfillment-group-merge-matcher,
 * fulfillment-group-actions, outbound-labels root, packing-slips) so the
 * OutboundLabelSettings slice can reference them without a model↔leaf cycle.
 * Each enum was dep-free; its origin file stays in redo/model and re-imports it.
 *
 * Persisted to Mongo — keep in sync with the `typedEnum` registrations in
 * `redo/backend/db/schema/src/team/OutboundLabelSettings.ts`.
 */

export enum WeightFormat {
  IMPERIAL = "imperial",
  METRIC = "metric",
}

/**
 * Which event in the scan-to-verify workflow auto-prints the personalized
 * insert. `OFF` disables auto-printing (the packer prints manually).
 */
export enum PersonalizedSlipAutoPrintTrigger {
  OFF = "off",
  SCAN_SCREEN_OPENED = "scan_screen_opened",
  ALL_ITEMS_VERIFIED = "all_items_verified",
  LABEL_PRINTED = "label_printed",
  PACKAGE_SELECTED = "package_selected",
}

export enum ServiceLevelPriority {
  CHEAPEST = "CHEAPEST",
  MOST_EXPENSIVE = "MOST_EXPENSIVE",
  OLDEST = "OLDEST",
  NEWEST = "NEWEST",
}

export enum SurchargeCategory {
  Weight = "weight",
  Dimensions = "dimensions",
  FuelSurcharge = "fuel_surcharge",
  DeliveryArea = "delivery_area",
  International = "international",
  AddressCorrection = "address_correction",
  PackageType = "package_type",
  Other = "other",
}

export enum LearnedPresetKind {
  CARRIER_SERVICE_LEVEL = "carrier-service-level",
  PARCEL = "parcel",
  WEIGHT = "weight",
}

export enum FulfillmentGroupMergeMatcherKind {
  SHIPPING_ADDRESS = "shipping-address",
  BILLING_ADDRESS = "billing-address",
  RECIPIENT = "recipient",
  CUSTOMER = "customer",
  CUSTOMER_NAME = "customer-name",
  ORIGIN_ADDRESS = "origin-address",
  PACKING_SLIP_UNPRINTED = "packing-slip-unprinted",
  PAYMENT_INFORMATION = "payment-information",
  STATUS = "status",
  OPEN_STATUS = "open-status",
  OPEN_OR_PAYMENT_PENDING_STATUS = "open-or-payment-pending-status",
  DELIVERY_METHOD = "delivery-method",
  LANDED_COST = "landed-cost",
  ORDER_ID_NOT_MATCH = "order-id-not-match",
}

export enum FulfillmentGroupActions {
  CREATE_LABEL = "Create label",
  CREATE_AND_PRINT_LABEL = "Create & print label",
  PRINT_LABEL = "Print label",
  PRINT_PACKING_SLIP = "Print packing slip",
  PRINT_PERSONALIZED_SLIP = "Print personalized slip",
  PRINT_PICK_LIST = "Print pick list",
  PRINT_ORDER_SUMMARY = "Print order summary",
  PRINT_LOCAL_PICKUP_LABEL = "Print local pickup label",
  EXPORT_PICK_LIST_CSV = "Export pick list CSV",
  COMMERCIAL_INVOICE = "Print commercial invoice",
  MARK_AS_FULFILLED = "Mark as fulfilled",
  READY_FOR_PICKUP = "Ready for pickup",
  MARK_AS_PICKED_UP = "Mark as picked up",
  PLACE_HOLD = "Place on hold",
  RELEASE_HOLD = "Release hold",
  UNMERGE = "Unmerge",
  ADD_FUNDS_AND_CREATE = "Add funds & purchase label",
  EDIT_ORDER = "Edit order details",
  NOTIFY_MARKETPLACE = "Notify marketplace",
  SUBMIT_CARRIER_CLAIM = "Submit carrier claim request",
  CREATE_RETURN_LABEL = "Create return label",
  PRINT_RETURN_LABEL = "Print return label",
  OVERRIDE_NOT_RECEIVED = "Override not received",
  REVERT_RECEIVED_OVERRIDE = "Revert not received override",
  RESOLVE_PROBLEM_ORDER = "Resolve unpicked items",
  BOOK_FREIGHT = "Book freight",
  PRINT_BOL = "Print BOL",
  CREATE_PURCHASE_ORDER = "Create purchase order",
  VIEW_PURCHASE_ORDER = "View purchase order",
  SCAN_AND_VERIFY = "Scan and verify",
  PRINT_PARCEL_PACKING_SLIPS = "Print package packing slips",
}

export enum PageFormat {
  Letter = "8.5x11",
  Label = "4x6",
}

export enum ItemOrderBy {
  NameAsc = "NameAsc",
  NameDesc = "NameDesc",
  QuantityAsc = "QuantityAsc",
  QuantityDesc = "QuantityDesc",
  SkuAsc = "SkuAsc",
  SkuDesc = "SkuDesc",
  TotalPriceAsc = "TotalPriceAsc",
  TotalPriceDesc = "TotalPriceDesc",
  UnitPriceAsc = "UnitPriceAsc",
  UnitPriceDesc = "UnitPriceDesc",
  BinAsc = "BinAsc",
  BinDesc = "BinDesc",
  OrdersAsc = "OrdersAsc",
  OrdersDesc = "OrdersDesc",
  VendorAsc = "VendorAsc",
  VendorDesc = "VendorDesc",
}

export enum PageOrientation {
  PORTRAIT = "portrait",
  LANDSCAPE = "landscape",
}
